import { apiService } from './api';
import type { CameraFeatureCollection } from './cameraOverlayService';

const http = apiService.client;

export type CameraOverlayFileDto = {
  id: number;
  name: string;
  feature_count: number;
  active: boolean;
  source?: string | null;
  category?: string | null;
  source_format?: string | null;
  uploaded_by?: string | null;
  uploaded_at: string;
  updated_at: string;
  /** Cameras with a derived road / intersection label. */
  location_count?: number;
};

/** Which source property feeds each normalized field (mirrors backend FieldMapping). */
export type CameraFieldMapping = {
  camera_id?: string | null;
  camera_name?: string | null;
  status?: string | null;
  category?: string | null;
  source?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  defaults?: Record<string, string>;
  keep_attributes?: boolean;
};

export const CAMERA_MAPPING_TARGETS: Array<{ key: keyof CameraFieldMapping; label: string; hint: string }> = [
  { key: 'camera_id', label: 'Camera ID', hint: 'Unique id; duplicates are dropped' },
  { key: 'camera_name', label: 'Name', hint: 'Shown as the map label' },
  { key: 'latitude', label: 'Latitude', hint: 'Only needed for CSV / JSON rows' },
  { key: 'longitude', label: 'Longitude', hint: 'Only needed for CSV / JSON rows' },
  { key: 'category', label: 'Category', hint: 'ALPR, traffic, police… inferred when blank' },
  { key: 'status', label: 'Status', hint: 'Active / inactive etc.' },
  { key: 'source', label: 'Source', hint: 'Per-row source; file-level source used when blank' },
];

export type CameraOverlayPreviewDto = {
  file_name: string;
  source_format: string;
  source_format_label: string;
  feature_count: number;
  skipped: number;
  fields: string[];
  mapping: CameraFieldMapping;
  source?: string | null;
  category?: string | null;
  category_counts: Record<string, number>;
  warnings: string[];
  sample: Array<GeoJSON.Feature<GeoJSON.Point, Record<string, unknown>>>;
};

export type CameraOverlayOptionsDto = {
  extensions: string[];
  formats: Record<string, string>;
  categories: Record<string, string>;
  mapping_targets: string[];
  max_bytes: number;
};

export type CameraOverlayImportOptions = {
  mapping?: CameraFieldMapping | null;
  source?: string | null;
  category?: string | null;
  /** Store under this name instead of the file name. */
  name?: string | null;
};

export const CAMERA_OVERLAY_ACCEPT =
  '.geojson,.json,.gpx,.kml,.kmz,.osm,.xml,.csv,.tsv,.txt,' +
  'application/geo+json,application/json,application/gpx+xml,' +
  'application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz,text/csv,text/xml';

export const CAMERA_OVERLAY_EXTENSIONS = [
  '.geojson', '.json', '.gpx', '.kml', '.kmz', '.osm', '.xml', '.csv', '.tsv', '.txt',
];

export function isSupportedCameraOverlayFile(name: string): boolean {
  const lower = name.toLowerCase();
  return CAMERA_OVERLAY_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/** Normalize FastAPI `detail` (string | object | array) for UI display. */
export function formatCameraOverlayApiError(err: unknown, fallback = 'Request failed'): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') {
        const msg = (item as { msg?: string }).msg;
        const loc = (item as { loc?: unknown[] }).loc;
        if (msg && Array.isArray(loc)) return `${loc.join('.')}: ${msg}`;
        if (msg) return msg;
      }
      try {
        return JSON.stringify(item);
      } catch {
        return String(item);
      }
    });
    const joined = parts.filter(Boolean).join('; ');
    if (joined) return joined;
  }
  if (detail && typeof detail === 'object') {
    try {
      return JSON.stringify(detail);
    } catch {
      /* fall through */
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export async function listCameraOverlays(): Promise<CameraOverlayFileDto[]> {
  const { data } = await http.get<{ files: CameraOverlayFileDto[] }>('/camera-overlays');
  return data.files ?? [];
}

export async function fetchCameraOverlayOptions(): Promise<CameraOverlayOptionsDto> {
  const { data } = await http.get<CameraOverlayOptionsDto>('/camera-overlays/options');
  return data;
}

export async function fetchCameraOverlaysGeoJson(
  ids?: number[] | null,
  categories?: string[] | null
): Promise<CameraFeatureCollection> {
  // undefined/null → all active files; [] → none; [1,2] → those ids
  const params: Record<string, string> = {};
  if (ids !== undefined && ids !== null) params.ids = ids.join(',');
  if (categories && categories.length) params.category = categories.join(',');
  const { data } = await http.get<CameraFeatureCollection>('/camera-overlays/geojson', {
    params: Object.keys(params).length ? params : undefined,
  });
  return data?.type === 'FeatureCollection'
    ? data
    : { type: 'FeatureCollection', features: [] };
}

function multipartHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem('access_token');
  if (token) headers.Authorization = `Bearer ${token}`;
  if (process.env.NODE_ENV === 'development') {
    const devUser = localStorage.getItem('dev_user');
    if (devUser) headers['X-Dev-User'] = devUser;
  }
  return headers;
}

function buildForm(file: File, opts: CameraOverlayImportOptions = {}): FormData {
  const form = new FormData();
  form.append('file', file);
  if (opts.mapping) {
    // Drop empty strings so the backend's auto-suggestion fills those slots.
    const clean: Record<string, unknown> = {};
    Object.entries(opts.mapping).forEach(([k, v]) => {
      if (v === '' || v === undefined) return;
      clean[k] = v;
    });
    form.append('mapping', JSON.stringify(clean));
  }
  if (opts.source && opts.source.trim()) form.append('source', opts.source.trim());
  if (opts.category && opts.category.trim()) form.append('category', opts.category.trim());
  if (opts.name && opts.name.trim()) form.append('name', opts.name.trim());
  return form;
}

async function postMultipart<T>(path: string, form: FormData): Promise<T> {
  // Use fetch (not axios) so the browser sets the multipart boundary.
  // ApiService defaults Content-Type to application/json, which breaks UploadFile.
  const base = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
  const response = await fetch(`${base}${path}`, {
    method: 'POST',
    headers: multipartHeaders(),
    body: form,
  });
  if (!response.ok) {
    let detail: unknown;
    try {
      const body = await response.json();
      detail = body?.detail ?? body;
    } catch {
      detail = await response.text().catch(() => null);
    }
    const err = new Error(
      typeof detail === 'string' && detail ? detail : `Request failed (${response.status})`
    ) as Error & { response: { data: { detail: unknown } } };
    err.response = { data: { detail } };
    throw err;
  }
  return (await response.json()) as T;
}

/** Parse without saving: format, fields, suggested mapping, category counts, sample. */
export async function previewCameraOverlayFile(
  file: File,
  opts: CameraOverlayImportOptions = {}
): Promise<CameraOverlayPreviewDto> {
  return postMultipart<CameraOverlayPreviewDto>('/camera-overlays/preview', buildForm(file, opts));
}

export async function uploadCameraOverlayFile(
  file: File,
  opts: CameraOverlayImportOptions = {}
): Promise<CameraOverlayFileDto> {
  return postMultipart<CameraOverlayFileDto>('/camera-overlays', buildForm(file, opts));
}

export type CameraOverlayPatch = {
  active?: boolean;
  /** Rename the overlay (must be unique). */
  name?: string;
  /** Applied to every camera in the file; null restores per-camera values. */
  source?: string | null;
  /** Applied to every camera in the file; null restores inferred categories. */
  category?: string | null;
};

export async function updateCameraOverlay(
  id: number,
  patch: CameraOverlayPatch
): Promise<CameraOverlayFileDto> {
  const { data } = await http.patch<CameraOverlayFileDto>(`/camera-overlays/${id}`, patch);
  return data;
}

export type CameraLocationJobDto = {
  overlay_id: number;
  /** idle | running | done | error */
  status: string;
  total: number;
  done: number;
  labeled: number;
  failed: number;
  error?: string | null;
  /** False when the server has neither a Mapbox token nor a Google Maps key for road lookups. */
  configured: boolean;
  /** 'mapbox' (intersection or road) | 'google' (nearest road, Street View key) | null. */
  provider?: 'mapbox' | 'google' | null;
  /** Labels already committed to the database (the job checkpoints as it goes). */
  saved?: number;
  /** Times the provider told us to slow down (the job pauses and resumes by itself). */
  rate_limited?: number;
  /** Short live status, e.g. while paused for a rate limit. */
  message?: string | null;
};

/** Kick off (or report the running) road-name derivation job for an overlay. */
export async function deriveCameraOverlayLocations(
  id: number,
  force = false
): Promise<CameraLocationJobDto> {
  const { data } = await http.post<CameraLocationJobDto>(
    `/camera-overlays/${id}/locations`,
    null,
    { params: force ? { force: true } : undefined }
  );
  return data;
}

export async function fetchCameraOverlayLocationJob(id: number): Promise<CameraLocationJobDto> {
  const { data } = await http.get<CameraLocationJobDto>(`/camera-overlays/${id}/locations`);
  return data;
}

export async function setCameraOverlayActive(
  id: number,
  active: boolean
): Promise<CameraOverlayFileDto> {
  return updateCameraOverlay(id, { active });
}

export async function deleteCameraOverlay(id: number): Promise<void> {
  await http.delete(`/camera-overlays/${id}`);
}
